import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ProgressRing } from "@/components/shared/progress-ring";
import { StatusBadge } from "@/components/shared/status-badge";
import { getLevelLabel, getTypeLabel } from "@/lib/status-machine";
import { ObjectiveKrList } from "@/features/objectives/objective-kr-list";
import { formatTimestamp } from "@/lib/date";
import { cn } from "@/lib/utils";
import type { Objective } from "@/types/entities";
import type { ObjectiveLadder } from "@/lib/objective-ladder";

interface ObjectiveCardProps {
  objective: Objective;
  ladder?: ObjectiveLadder | null;
}

export function LadderBreadcrumb({ ladder }: { ladder: ObjectiveLadder }) {
  const segments: { label: string; full: string }[] = [];

  if (ladder.pillar) {
    segments.push({ label: ladder.pillar.name, full: ladder.pillar.name });
  }
  if (ladder.goal) {
    segments.push({ label: ladder.goal.name, full: ladder.goal.name });
  }
  segments.push({ label: ladder.focusArea.name, full: ladder.focusArea.name });
  if (ladder.strategicInitiative) {
    segments.push({
      label: ladder.strategicInitiative.name,
      full: ladder.strategicInitiative.name,
    });
  }

  return (
    <div className="flex min-w-0 items-center gap-1 text-xs text-[hsl(var(--muted-foreground))]">
      {segments.map((seg, i) => (
        <span key={i} className="flex min-w-0 items-center gap-1">
          {i > 0 && <span className="shrink-0">›</span>}
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="truncate max-w-[10rem]">{seg.label}</span>
            </TooltipTrigger>
            <TooltipContent>{seg.full}</TooltipContent>
          </Tooltip>
        </span>
      ))}
    </div>
  );
}

function KrMiniBar({
  total,
  completed,
  percent,
}: {
  total: number;
  completed: number;
  percent: number | null;
}) {
  const displayPercent = Math.round(percent ?? 0);
  return (
    <div className="hidden w-32 flex-col gap-1 lg:flex" aria-hidden={percent == null}>
      <div className="flex items-center justify-between text-[11px] text-[hsl(var(--muted-foreground))]">
        <span>
          {completed} / {total} KRs done
        </span>
        <span className="font-bold tabular-nums text-[hsl(var(--foreground))]">
          {percent == null ? "—" : `${displayPercent}%`}
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-[hsl(var(--border))]">
        <div
          className="h-full rounded-full bg-[hsl(var(--primary))] transition-[width] duration-300"
          style={{ width: percent == null ? 0 : `${displayPercent}%` }}
        />
      </div>
    </div>
  );
}

export function ObjectiveCard({ objective, ladder }: ObjectiveCardProps) {
  const [expanded, setExpanded] = useState(false);
  const krTotal = objective.kr_total ?? 0;
  const krCompleted = objective.kr_completed ?? 0;
  const completionPercent = objective.completion_percent ?? null;
  const ringValue = completionPercent ?? 0;

  return (
    <Card
      className={cn(
        "transition-all duration-200 hover:-translate-y-px hover:border-[hsl(var(--primary))] hover:shadow-md",
        expanded && "border-[hsl(var(--primary))] shadow-md",
      )}
    >
      <CardHeader className="px-4 py-3">
        <div className="flex items-start gap-3">
          <Button
            variant="ghost"
            size="sm"
            className="mt-0.5 h-6 w-6 shrink-0 p-0"
            onClick={() => setExpanded((prev) => !prev)}
            aria-label={expanded ? "Collapse key results" : "Expand key results"}
            aria-expanded={expanded}
          >
            <ChevronRight
              className={cn(
                "h-4 w-4 transition-transform duration-200",
                expanded && "rotate-90",
              )}
            />
          </Button>
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <CardTitle className="break-words text-base font-semibold leading-snug">
              <Link
                to="/objectives/$id"
                params={{ id: objective.id }}
                className="hover:underline"
              >
                {objective.title}
              </Link>
            </CardTitle>
            {objective.description && (
              <p className="line-clamp-1 text-sm text-[hsl(var(--muted-foreground))]">
                {objective.description}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs text-[hsl(var(--muted-foreground))]">
              {objective.owner_name && (
                <span className="font-medium text-[hsl(var(--foreground))]">
                  {objective.owner_name}
                </span>
              )}
              {objective.owner_name && ladder && <span aria-hidden="true">·</span>}
              {ladder && <LadderBreadcrumb ladder={ladder} />}
              {(objective.owner_name || ladder) && <span aria-hidden="true">·</span>}
              <span>Updated {formatTimestamp(objective.updated_at)}</span>
            </div>
          </div>
          <KrMiniBar total={krTotal} completed={krCompleted} percent={completionPercent} />
          <ProgressRing
            value={ringValue}
            ariaLabel={
              completionPercent == null
                ? "No progress yet"
                : `${Math.round(completionPercent)}% complete`
            }
          />
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
            <Badge variant="outline" className="rounded">
              {objective.year}
            </Badge>
            <Badge variant="meta-level" className="rounded">
              {getLevelLabel(objective.level)}
            </Badge>
            <Badge variant="meta-type" className="rounded">
              {getTypeLabel(objective.type)}
            </Badge>
          </div>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="border-t border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.3)] px-4 pb-4 pt-3">
          <div className="mb-3 flex items-center justify-between gap-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
              Key Results · {krTotal}
            </span>
            <StatusBadge status={objective.status} />
          </div>
          <ObjectiveKrList
            objectiveId={objective.id}
            ownerId={objective.owner_id}
            year={objective.year}
          />
        </CardContent>
      )}
    </Card>
  );
}
